import { useState, useEffect } from "react";
import { initPostHog } from "@/lib/monitoring/posthog";
import { Link } from "react-router-dom";
import { useFocusTrap } from "@/hooks/useFocusTrap";

const CONSENT_KEY = "mp_consent";

export type ConsentValue = "accepted" | "declined" | null;

export function getStoredConsent(): ConsentValue {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    if (v === "accepted" || v === "declined") return v;
  } catch {}
  return null;
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const dialogRef = useFocusTrap<HTMLDivElement>(visible);

  useEffect(() => {
    // Show banner only if no decision has been stored yet
    if (getStoredConsent() === null) {
      setVisible(true);
    }
  }, []);

  function accept() {
    try { localStorage.setItem(CONSENT_KEY, "accepted"); } catch {}
    initPostHog();
    setVisible(false);
  }

  function decline() {
    try { localStorage.setItem(CONSENT_KEY, "declined"); } catch {}
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-0 inset-x-0 z-[9000] p-4 sm:p-6 flex justify-center"
    >
      <div className="w-full max-w-2xl bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <p className="flex-1 text-sm text-zinc-300 leading-relaxed">
          We use analytics cookies (PostHog) to improve the app. Error
          monitoring (Sentry) is always active but contains no personal data.{" "}
          <Link to="/privacy-policy" className="text-[rgb(var(--cs))] hover:underline">
            Privacy Policy
          </Link>
        </p>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={decline}
            className="px-4 py-2 rounded-lg text-sm font-medium text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-500 transition-colors"
          >
            Decline
          </button>
          <button
            onClick={accept}
            className="px-4 py-2 rounded-lg text-sm font-bold bg-[rgb(var(--cp))] hover:bg-[rgb(var(--cp)/0.85)] text-white transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
